import { assert } from '../util/invariant';

/**
 * In development mode do a quick check to verify that an unknown value the
 * object type that we expect.
 *
 * This does not perform thorough check and does no checking in production
 * builds. Therefore only use this function for the purpose of catching
 * programming mistakes during development.
 *
 * If you specify a property key to check, then make sure to pick a property key
 * you expect never to be omitted. I.e. don't pick an optional property, unless
 * you always expect the property to be set despite it being optional.
 */
export function assumeObject<T = Record<string, never>>(obj: unknown, checkKey?: keyof T): T {
  assert(obj != null, 'Expected object, got ' + obj);
  assert(typeof obj === 'object', 'Expected object, got ' + typeof obj);
  assert(!Array.isArray(obj), 'Expected plain object, got array');
  // eslint-disable-next-line @typescript-eslint/ban-types
  assert(checkKey === undefined || (typeof obj === 'object' && obj != null && checkKey in obj!), () => 'Expected object with prop ' + String(checkKey) + ', got ' + Object.keys(obj as object).join(', ') || 'no props');
  return obj as unknown as T;
}

export function assumeArray<T = unknown>(obj: unknown): T[] {
  assert(Array.isArray(obj), 'Expected array, got non-array ' + typeof obj);
  return obj as T[];
}

export function assumeArrayOfObject<T = unknown>(obj: unknown, checkKey?: keyof T): T[] {
  assert(Array.isArray(obj), 'Expected array, got non-array ' + typeof obj);
  if (obj.length > 0 && checkKey != null) {
    assumeObject(obj[0], checkKey);
  }
  return obj as T[];
}

export function assumeString(obj: unknown): string {
  assert(typeof obj === 'string', 'Expected string, got ' + typeof obj);
  return obj;
}

export function assumeBoolean(obj: unknown): boolean {
  assert(typeof obj === 'boolean', 'Expected boolean, got ' + typeof obj);
  return obj;
}

export function assumeHalCollection<T = unknown>(obj: unknown): T[] {
  assert(obj != null, 'Expected object, got ' + obj);
  assert(typeof obj === 'object', 'Expected object, got ' + typeof obj);
  const embedded = (obj as any)._embedded;
  assert(embedded != null, 'Expected _embedded object, got ' + embedded);
  assert(typeof embedded === 'object', 'Expected _embedded object, got ' + typeof embedded);
  const collection = (embedded as any).collection;
  assert(Array.isArray(collection), 'Expected _embedded.collection array, got non-array ' + typeof collection);
  return collection;
}
